// Welcome popup timing and behavior (markup: sections/jrdg-welcome-popup.liquid).
// Desktop opens after data-delay-desktop seconds or on exit intent; phones after data-delay-mobile seconds or data-scroll %.
// Never opens again after a signup, stays hidden data-days after a close, and skips visitors who arrived from an email.
// After the form posts, Shopify reloads the page with #jrdg-welcome-form and the popup reopens on the result.
(() => {
  if (customElements.get('jrdg-welcome-popup')) return;

  const KEY_SUBSCRIBED = 'jrdg-wp-subscribed';
  const KEY_CLOSED = 'jrdg-wp-closed-at';
  const KEY_FROM_EMAIL = 'jrdg-wp-from-email';
  const store = (area, key, value) => {
    try {
      if (value === undefined) return window[area].getItem(key);
      window[area].setItem(key, value);
    } catch (e) {
      return null;
    }
    return null;
  };

  const cameFromEmail = () => {
    const params = new URLSearchParams(location.search);
    const source = (params.get('utm_source') || '').toLowerCase();
    return (params.get('utm_medium') || '').toLowerCase() === 'email' || /omnisend|shopify_email|mailchimp|klaviyo/.test(source);
  };

  // US numbers to +1XXXXXXXXXX so Shopify accepts them; anything else passes through as typed.
  const normalizePhone = (value) => {
    const digits = value.replace(/\D/g, '');
    if (!digits) return '';
    if (value.trim().startsWith('+')) return `+${digits}`;
    if (digits.length === 10) return `+1${digits}`;
    if (digits.length === 11 && digits.startsWith('1')) return `+${digits}`;
    return value.trim();
  };

  class JrdgWelcomePopup extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('.jrdg-wp');
      this.form = this.querySelector('form');
      if (!this.dialog) return;
      this.onKeydown = this.onKeydown.bind(this);
      this.onExit = this.onExit.bind(this);
      this.onScroll = this.onScroll.bind(this);

      this.querySelectorAll('[data-wp-close]').forEach((button) => button.addEventListener('click', () => this.close()));
      this.dialog.addEventListener('click', (event) => {
        if (event.target === this.dialog) this.close();
      });
      if (this.form) this.form.addEventListener('submit', (event) => this.onSubmit(event));
      const copyButton = this.querySelector('[data-wp-copy]');
      if (copyButton) copyButton.addEventListener('click', () => this.copyCode(copyButton));

      if (this.hasAttribute('data-design-mode')) {
        document.addEventListener('shopify:section:select', (event) => {
          if (this.closest('.shopify-section') === event.target) this.open();
        });
        document.addEventListener('shopify:section:deselect', (event) => {
          if (this.closest('.shopify-section') === event.target) this.close(false);
        });
        return;
      }

      if (cameFromEmail()) store('sessionStorage', KEY_FROM_EMAIL, '1');

      const stateEl = this.querySelector('[data-wp-state]');
      const state = stateEl ? stateEl.dataset.wpState : '';
      const ours = location.hash === '#jrdg-welcome-form';
      if (state === 'posted') store('localStorage', KEY_SUBSCRIBED, '1');
      if (ours && (state === 'posted' || state === 'error')) {
        this.open();
        return;
      }
      if (this.suppressed()) return;
      this.arm();
    }

    suppressed() {
      if (store('localStorage', KEY_SUBSCRIBED)) return true;
      if (store('sessionStorage', KEY_FROM_EMAIL)) return true;
      const closedAt = Number(store('localStorage', KEY_CLOSED) || 0);
      const days = Number(this.dataset.days || 7);
      return closedAt > 0 && Date.now() - closedAt < days * 86400000;
    }

    isPhone() {
      return window.matchMedia('(max-width: 749px)').matches;
    }

    arm() {
      const phone = this.isPhone();
      const delay = Number(phone ? this.dataset.delayMobile : this.dataset.delayDesktop) * 1000 || 10000;
      this.timer = setTimeout(() => this.trigger(), delay);
      if (phone) {
        window.addEventListener('scroll', this.onScroll, { passive: true });
      } else {
        document.addEventListener('mouseout', this.onExit);
      }
    }

    disarm() {
      clearTimeout(this.timer);
      clearTimeout(this.retry);
      window.removeEventListener('scroll', this.onScroll);
      document.removeEventListener('mouseout', this.onExit);
    }

    onExit(event) {
      if (!event.relatedTarget && event.clientY <= 0) this.trigger();
    }

    onScroll() {
      const room = document.documentElement.scrollHeight - window.innerHeight;
      if (room > 0 && window.scrollY / room >= Number(this.dataset.scroll || 50) / 100) this.trigger();
    }

    // Wait while the cart drawer, menu or search is open (Dawn locks the page with overflow-hidden).
    trigger() {
      if (this.isOpen) return;
      if (document.body.classList.contains('overflow-hidden') || document.querySelector('cart-drawer.active')) {
        clearTimeout(this.retry);
        this.retry = setTimeout(() => this.trigger(), 3000);
        return;
      }
      this.disarm();
      this.open();
    }

    open() {
      if (this.isOpen) return;
      this.isOpen = true;
      this.lastFocus = document.activeElement;
      this.dialog.hidden = false;
      this.classList.add('is-open');
      document.addEventListener('keydown', this.onKeydown);
      let target = this.dialog.querySelector('[data-wp-copy]');
      if (!target) {
        target = this.dialog.querySelector('.jrdg-wp__error:not([hidden])')
          ? this.dialog.querySelector('input[name="contact[phone]"], input[name="contact[first_name]"]')
          : this.dialog.querySelector('input:not([type=hidden]), .jrdg-wp__submit');
      }
      // After a signup the popup opens while the page is still loading, and focus can get reset on load; focus once it settles.
      const focusTarget = () => {
        if (target && this.isOpen) target.focus({ preventScroll: true });
      };
      if (document.readyState === 'complete') {
        focusTarget();
      } else {
        window.addEventListener('load', () => setTimeout(focusTarget, 0), { once: true });
      }
    }

    close(remember = true) {
      if (!this.isOpen) return;
      this.isOpen = false;
      this.classList.remove('is-open');
      this.dialog.hidden = true;
      document.removeEventListener('keydown', this.onKeydown);
      if (remember && !store('localStorage', KEY_SUBSCRIBED)) store('localStorage', KEY_CLOSED, String(Date.now()));
      if (location.hash === '#jrdg-welcome-form') history.replaceState(null, '', location.pathname + location.search);
      if (this.lastFocus && this.lastFocus.focus) this.lastFocus.focus({ preventScroll: true });
    }

    onKeydown(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
        return;
      }
      if (event.key !== 'Tab') return;
      const focusable = [...this.dialog.querySelectorAll('button, input:not([type=hidden]), a[href]')].filter((el) => !el.disabled && el.offsetParent !== null);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    // Clipboard API first; select-and-copy fallback for browsers that block it (older iOS, embedded browsers).
    copyCode(button) {
      const codeEl = this.querySelector('[data-wp-code]');
      if (!codeEl) return;
      const done = () => {
        button.textContent = 'Copied';
        clearTimeout(this.copyReset);
        this.copyReset = setTimeout(() => (button.textContent = 'Copy code'), 2500);
      };
      const fallback = () => {
        const range = document.createRange();
        range.selectNodeContents(codeEl);
        const selection = window.getSelection();
        selection.removeAllRanges();
        selection.addRange(range);
        try {
          document.execCommand('copy');
        } catch (e) {
          // The code stays selected so the shopper can copy it by hand.
        }
        done();
      };
      const code = codeEl.textContent.trim();
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(code).then(done, fallback);
      } else {
        fallback();
      }
    }

    onSubmit(event) {
      const name = this.form.querySelector('[name="contact[first_name]"]');
      const email = this.form.querySelector('[name="contact[email]"]');
      const phone = this.form.querySelector('[name="contact[phone]"]');
      const last = this.form.querySelector('[data-wp-last]');
      const invalid = this.form.querySelector('[data-wp-invalid]');
      const full = (name.value || '').trim().replace(/\s+/g, ' ');
      const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((email.value || '').trim());
      if (!full || !emailOk) {
        event.preventDefault();
        if (invalid) invalid.hidden = false;
        (full ? email : name).focus();
        return;
      }
      const [firstName, ...rest] = full.split(' ');
      name.value = firstName;
      if (last) last.value = rest.join(' ');
      if (phone) {
        phone.value = normalizePhone(phone.value || '');
        if (!phone.value) phone.disabled = true; // an empty phone is left out of the post entirely
      }
    }
  }

  customElements.define('jrdg-welcome-popup', JrdgWelcomePopup);
})();
